import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Send, Square } from 'lucide-react';

type Props = {
  input: string;
  setInput: (val: string) => void;
  onSend: () => void;
  isGenerating: boolean;
  disabled: boolean;
  onStopGeneration: () => void;
};

type ChatInputRef = {
  focus: () => void;
};

export const ChatInput = forwardRef<ChatInputRef, Props>(({ input, setInput, onSend, isGenerating, disabled, onStopGeneration }, ref) => {
  const [inputHeight, setInputHeight] = useState<'1row' | '3rows'>('1row');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // 暴露focus方法给父组件
  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
    }
  }));
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isGenerating && input.trim() && !disabled) {
        onSend();
      }
    }
  };
  
  // 监听输入内容变化，调整输入框高度
  useEffect(() => {
    if (input.trim()) {
      setInputHeight('3rows');
    } else {
      setInputHeight('1row');
    }
  }, [input]);

  return (
    <div className="relative">
      <textarea 
        ref={textareaRef}
        value={input} 
        onChange={e => setInput(e.target.value)} 
        onKeyDown={handleKeyDown} 
        placeholder="Type here..." 
        className={`w-full pl-4 pr-12 py-3 rounded-xl border border-gray-200 resize-none max-h-[150px] bg-white text-sm shadow-sm focus:border-brand-500 outline-none transition-all duration-200 ${
          inputHeight === '1row' ? 'h-[50px]' : 'h-[100px]'
        }`} 
        disabled={disabled} 
      />
      {isGenerating ? (
        <button
          onClick={onStopGeneration}
          className="absolute right-2 top-2 p-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
          title="停止生成"
        >
          <Square size={20} />
        </button>
      ) : (
        <button
          onClick={onSend}
          disabled={!input.trim() || disabled}
          className="absolute right-2 top-2 p-2 text-brand-600 hover:bg-brand-50 rounded-lg disabled:opacity-30 transition-colors"
          title="发送消息"
        >
          <Send size={20}/>
        </button>
      )}
    </div>
  );
});

ChatInput.displayName = 'ChatInput';
